<svelte:options
  customElement={{
    tag: 'dynamic-svelte-news-listing',
    props: {
      newsId: { type: 'String' },
      baseApiUrl: { type: 'String' },
      apiUrl: { type: 'String' },
      language: { type: 'String' },
      dictionary: { type: 'Object' },
    },
    shadow: 'none',
  }}
/>

<script>
  /* eslint-disable compat/compat */

  import { SvelteURLSearchParams } from 'svelte/reactivity';
  import Pagination from '../pagination/pagination.svelte';
  import Icon from '../icon/icon.svelte';

  let { newsId, language = 'en-US', dictionary, baseApiUrl = '' } = $props();

  let apiUrl = $derived(baseApiUrl + '/api/news/' + newsId);

  const COUNT_PER_PAGE = 12;

  let urlParams = new SvelteURLSearchParams(window.location.search);
  let currentPage = $state(parseInt(urlParams.get('page') ?? '1') - 1);
  let searchInput = $state(urlParams.get('search'));

  let filters = $state({
    order: urlParams.get('order') || 'latest',
  });

  function onSearchSubmit(e) {
    e.preventDefault();
    currentPage = 0;
    urlParams.set('page', 1);
    urlParams.set('search', searchInput);
    const newPath = '?' + urlParams.toString();
    history.pushState({}, '', newPath);
    fetchSearch();
  }

  function getParams() {
    const params = new SvelteURLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      params.append(key, value);
    });

    params.append('page', currentPage);
    params.append('count', COUNT_PER_PAGE);
    params.append('lang', language); // Versive add current language

    if (searchInput) {
      params.append('search', searchInput);
    }

    return params;
  }

  let finishedInitialSearch = $state(false);
  let listingItems = $state([]);
  let totalResults = $state(0);

  async function fetchSearch() {
    const params = getParams();
    let url = apiUrl + '?' + params.toString();

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      finishedInitialSearch = true;

      listingItems = data.items;
      totalResults = data.totalCount;
    } catch (error) {
      console.error('Fetch error:', error);
    }
  }

  function onPaginationClick(number) {
    currentPage = number;
    urlParams.set('page', number + 1);
    const newPath = '?' + urlParams.toString();
    history.pushState({}, '', newPath);
    fetchSearch();
  }

  function onFiltersChange() {
    currentPage = 0;
    urlParams.set('page', 1);

    Object.entries(filters).forEach(([key, value]) => {
      urlParams.set(key, value);
    });

    updateUrl();
  }

  function updateUrl() {
    const newPath = '?' + urlParams.toString();
    history.pushState({}, '', newPath);
    fetchSearch();
  }

  fetchSearch();
</script>

<section
  class="news-listing__listing"
  data-news
  data-newsid={newsId}
  data-lang={language}
  aria-live="polite"
>
  <form class="news-listing__filters" onsubmit={onSearchSubmit}>
    <div class="news-listing__search">
      <input
        class="input"
        type="text"
        name="search"
        aria-label="Search bar"
        data-news-search-input
        bind:value={searchInput}
        placeholder={dictionary.Search}
      />
      <button
        class="news-listing__search-button"
        data-news-search-button
        title={dictionary.Search}
      >
        <Icon class="icon" width="24" height="24" icon="search"></Icon>
      </button>
    </div>

    <div class="news-listing__sort">
      <label for="order" class="news-listing__label">{dictionary.SortBy}: </label>
      <div class="select-wrapper">
        <select
          id="order"
          data-news-filter
          class="news-listing__dropdown select"
          name="order"
          bind:value={filters.order}
          onchange={onFiltersChange}
        >
          <option value="latest">{dictionary.LatesttoOldest}</option>
          <option value="oldest">{dictionary.OldesttoLatest}</option>
        </select>
      </div>
    </div>
  </form>

  {#if finishedInitialSearch}
    {#if totalResults === 0}
      <div
        class="no-result search-listing__no-results displaynone"
        style="padding: 50px;min-height:150px;text-align:center;"
      >
        {dictionary.NoResults}
      </div>
    {:else}
      <ul class="news-listing__list news-results" data-news-listing>
        {#each listingItems as item (item.link)}
          <li>
            <!-- this is a simple-card component with modifications
                 TODO: this should accept responsive picture sizes 
            -->
            <div class="simple-card">
              <div class="card-image-wrapper">
                {#if item.image}
                  <picture>
                    <img src={item.image} alt={item.title} />
                  </picture>
                {/if}
                {#if item.video && !item.image}
                  <figure class="video-embed">
                    <div
                      class="video-embed__video"
                      data-video-url={item.video}
                    ></div>
                  </figure>
                {/if}
              </div>

              <div class="card-content">
                <!-- custom from default card -->
                <div class="card__meta">
                  <span class="card__tag">{item.tag}</span>
                  <span class="card__date">{item.date}</span>
                </div>
                <h3 class="card-title">
                  <a href={item.link} class="link-area highlighted-title-text">
                    {item.title}
                  </a>
                </h3>

                <div class="card-copy">
                  {item.copy}
                </div>

                <Icon
                  class="icon news-listing__icon"
                  width="22"
                  height="24"
                  icon="arrow-right"
                />
              </div>
            </div>
          </li>
        {/each}
      </ul>
      <Pagination
        totalCount={totalResults}
        countPerPage={COUNT_PER_PAGE}
        {currentPage}
        {dictionary}
        onClick={onPaginationClick}
      />
    {/if}
  {/if}
</section>
